/* Lightweight password-strength meter — no external deps.
 *
 * Mirrors the zxcvbn 0..4 score scale (very weak → strong) but uses a small
 * hand-rolled heuristic so we don't add ~400KB to the bundle. Returns the
 * score plus a one-line suggestion the user can act on.
 */
import React, { useMemo } from 'react';

const COMMON = new Set([
  'password', 'password1', 'passw0rd', '12345678', '123456789', '1234567890',
  'qwerty', 'qwerty123', 'iloveyou', 'admin', 'welcome', 'letmein', 'monkey',
  'dragon', 'football', 'baseball', 'sunshine', 'princess', 'abc12345',
  '11111111', '00000000', 'aaaaaaaa', 'master', 'starwars',
]);

function classes(pw) {
  return {
    lower:  /[a-z]/.test(pw),
    upper:  /[A-Z]/.test(pw),
    digit:  /\d/.test(pw),
    symbol: /[^A-Za-z0-9]/.test(pw),
  };
}

function hasSequence(pw) {
  // Detect 4+ consecutive alpha/numeric runs (e.g., abcd, 1234, 4321)
  const s = pw.toLowerCase();
  for (let i = 0; i < s.length - 3; i++) {
    const a = s.charCodeAt(i), b = s.charCodeAt(i + 1),
          c = s.charCodeAt(i + 2), d = s.charCodeAt(i + 3);
    if (b - a === 1 && c - b === 1 && d - c === 1) return true;
    if (a - b === 1 && b - c === 1 && c - d === 1) return true;
  }
  return false;
}

function hasRepeats(pw) {
  return /(.)\1{2,}/.test(pw); // 3+ same char in a row
}

export function scorePassword(pw) {
  if (!pw) return { score: 0, label: 'Empty', suggestion: 'Enter a password.', pct: 0 };
  const lower = pw.toLowerCase();
  if (COMMON.has(lower)) {
    return { score: 0, label: 'Very weak', suggestion: 'That password is on every breach list — pick something unique.', pct: 8 };
  }
  let score = 0;
  // Length tiers
  if (pw.length >= 8)  score += 1;
  if (pw.length >= 12) score += 1;
  if (pw.length >= 16) score += 1;
  // Class diversity
  const c = classes(pw);
  const classCount = Object.values(c).filter(Boolean).length;
  if (classCount >= 2) score += 0.5;
  if (classCount >= 3) score += 0.5;
  if (classCount === 4) score += 0.5;
  // Penalties
  if (hasSequence(pw)) score -= 0.5;
  if (hasRepeats(pw))  score -= 0.5;
  if (pw.length < 8)   score = Math.min(score, 0.5);

  score = Math.max(0, Math.min(4, Math.round(score)));

  const suggestion = (() => {
    if (pw.length < 8) return 'Use at least 8 characters.';
    if (classCount < 2) return 'Mix letters, numbers, and symbols.';
    if (hasSequence(pw)) return 'Avoid sequences like 1234 or abcd.';
    if (hasRepeats(pw))  return 'Avoid repeating the same character.';
    if (pw.length < 12)  return 'Longer is stronger — aim for 12+ characters.';
    if (score < 4)       return 'Add more variety or length for a stronger password.';
    return 'Nice — looks strong.';
  })();

  const label = ['Very weak', 'Weak', 'Fair', 'Good', 'Strong'][score];
  const pct   = [10, 30, 55, 80, 100][score];
  return { score, label, suggestion, pct };
}

const BAR_COLOR = [
  'bg-destructive',         // 0 very weak
  'bg-destructive/80',      // 1 weak
  'bg-warning',             // 2 fair
  'bg-success/80',          // 3 good
  'bg-success',             // 4 strong
];
const LABEL_COLOR = [
  'text-destructive',
  'text-destructive',
  'text-warning',
  'text-success',
  'text-success',
];

export default function PasswordStrengthMeter({ password, className = '' }) {
  const { score, label, suggestion, pct } = useMemo(() => scorePassword(password || ''), [password]);
  if (!password) return null;
  return (
    <div className={`mt-2 ${className}`} data-testid="pw-strength-meter">
      <div className="flex items-center justify-between text-[10.5px]">
        <span className="uppercase tracking-[0.18em] text-muted-foreground">Strength</span>
        <span className={`font-semibold ${LABEL_COLOR[score]}`} data-testid="pw-strength-label">{label}</span>
      </div>
      <div className="mt-1 h-1.5 w-full overflow-hidden rounded-full bg-secondary">
        <div
          className={`h-full transition-all duration-300 ${BAR_COLOR[score]}`}
          style={{ width: `${pct}%` }}
          data-testid="pw-strength-bar"
        />
      </div>
      <p className="mt-1.5 text-[11px] text-muted-foreground" data-testid="pw-strength-suggestion">
        {suggestion}
      </p>
    </div>
  );
}
